import { MapMouseEvent, useMap } from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import GoogleMap from "../../../../contexts/WorldMapContext/GoogleMap";
import { markerByName, PointMarker } from "../../../../contexts/WorldMapContext/Marker";
import { DiseaseZonePolygon } from "../../../../contexts/WorldMapContext/Polygon";
import { colors, SeaWayPolyline } from "../../../../contexts/WorldMapContext/Polyline";
import { BarentsWatchService } from "../../../../util/barentswatch/api";
import { datetime } from "../../../../util/library/datetime";
import { useSelector } from "../../../../util/store/store";
import { diseasedZones } from "../../../../util/varibles/constants";
import { convertDisease } from "../../../../util/varibles/global";
import { IRoute } from "../../../../util/varibles/interface";
import { RouteMode } from "../../constatns";
import { routeActions } from "../../reducer";

const Seaway: React.FC<{
    data: IRoute,
    routeIndex: number,
}> = ({ data, routeIndex }) => {
    const mode = useSelector(state => state.route.mode);
    const showId = useSelector(state => state.route.showId);
    const isShowAll = useSelector(state => state.route.isShowAll);

    const { id, points } = data;
    const color = colors[routeIndex];

    const seaway = points.map((item, subI) => ({
        id: subI,
        color,
        lat: item.latitude,
        lng: item.longitude,
    }))

    return <>
        <SeaWayPolyline
            key={id}
            data={{ points: seaway }}
            show={mode === RouteMode.VIEW && (isShowAll || showId === routeIndex)}
            width={showId === routeIndex ? 3 : 1}
            color={color}
        />
        {seaway.map((point, i) => (i && (i < seaway.length - 1)) ? <PointMarker
            key={point.id}
            show={mode === RouteMode.VIEW && (isShowAll || showId === routeIndex)}
            data={point}

        /> : null)}
    </>
}

const SeawayEdit = () => {
    const route = useSelector(state => state.route.route);
    const mode = useSelector(state => state.route.mode);
    const dispatch = useDispatch();

    const { id, points = [], index = 0 } = route || {};
    const color = colors[index];
    const seaway = points.map((item, subI) => ({
        id: subI,
        name: `${subI}`,
        color,
        lat: item.latitude,
        lng: item.longitude,
    }))

    const handleSeawayClick = useCallback((e: google.maps.MapMouseEvent) => {
        const { latLng } = e;
        if (!latLng)
            return;
        const latitude = latLng.lat() || 0;
        const longitude = latLng.lng() || 0;
        const position = { minDistance: 9999999999, index: -1 };
        for (let i = 0, len = seaway.length - 1; i < len; i++) {
            const dist = google.maps.geometry.spherical.computeDistanceBetween(seaway[i], latLng);
            const dist1 = google.maps.geometry.spherical.computeDistanceBetween(seaway[i + 1], latLng);
            const dist2 = google.maps.geometry.spherical.computeDistanceBetween(seaway[i], seaway[i + 1]);
            const minDistance = dist + dist1 - dist2;
            if (minDistance < position.minDistance) {
                position.minDistance = minDistance;
                position.index = i + 1;
            }
        }
        dispatch(routeActions.updatePoint({ marker: { latitude, longitude }, position: position.index }))
    }, [dispatch, seaway]);

    const handleUpdate = useCallback((subI: number, e: google.maps.MapMouseEvent) => {
        const latitude = e.latLng?.lat() || 0;
        const longitude = e.latLng?.lng() || 0;
        dispatch(routeActions.updatePoint({ marker: { latitude, longitude }, position: subI, isDelete: true }))
    }, [dispatch]);

    const handleDelete = useCallback((index: number) => {
        if (!route) return;

        dispatch(routeActions.editRoute({
            ...route,
            points: route.points.filter((_, pointIndex) => pointIndex !== index)
        }));
    }, [dispatch, route]);

    return <>
        <SeaWayPolyline
            key={id}
            data={{ points: seaway }}
            show={mode === RouteMode.EDIT}
            width={3}
            color={color}
            onContextMenu={handleSeawayClick}
        />
        {seaway.map((point, i) => (i && (i < seaway.length - 1)) ? <PointMarker
            key={point.id}
            data={point}
            show={mode === RouteMode.EDIT}
            onContextmenu={e => {
                e?.preventDefault();
                e?.stopPropagation();
                handleDelete(i);
            }}
            onUpdate={e => handleUpdate(i, e)}
        /> : null)}
    </>
}

const Seaways: React.FC = () => {
    const routes = useSelector(state => state.route.routes);

    return <>
        {routes.map((item, i) => <Seaway key={item.id} data={item} routeIndex={i} />)}
    </>
}

const Locations: React.FC = () => {
    const { source, destination } = useSelector(state => state.route.params);
    const map = useMap();

    useEffect(() => {
        if (source && destination) {
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(new google.maps.LatLng(source.position?.latitude, source.position?.longitude));
            bounds.extend(new google.maps.LatLng(destination.position?.latitude, destination.position?.longitude));
            map?.fitBounds(bounds);
        }
    }, [source, destination, map]);

    return <>
        {[source, destination].map(item => {
            const { field } = item || {};
            const Marker = markerByName[field];
            if (Marker)
                return <Marker key={item.id} data={item} />
            return null;
        })}
    </>
}

const DiseaseZone: React.FC<{ id: any }> = ({ id }) => {
    const [zones, setZones] = useState<any[]>([]);

    useEffect(() => {
        const weekNew = [datetime().year, datetime().week].join('/');
        BarentsWatchService.getIlaControlAreaV2(id, weekNew)
            .then((rs: any) => {
                const zones = rs.map((item: any) => convertDisease(item));
                setZones(zones);
            })
            .catch(() => {
            })
    }, [id])

    return <>
        {zones.map(item => <DiseaseZonePolygon
            key={item.id}
            data={item}
            show={true}
            diseaseType={id}
        />)}
    </>;
}

const Map: React.FC = () => {
    const mode = useSelector(state => state.route.mode);
    const dispatch = useDispatch();

    useEffect(() => {

    }, []);

    const handleMapClick = useCallback((event?: MapMouseEvent) => {
        const { lat = 0, lng = 0 } = event?.detail.latLng || {};
        dispatch(routeActions.updatePoint({ marker: { latitude: lat, longitude: lng } }))
    }, [dispatch]);

    return <div className='h-full w-full'>
        <GoogleMap onContextmenu={mode !== RouteMode.EDIT ? undefined : handleMapClick}>
            <Seaways />
            <SeawayEdit />
            <Locations />
            {diseasedZones.map(item => <DiseaseZone key={item.id} id={item.id} />)}
        </GoogleMap>
    </div>;
};

export default Map;
